import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaRupeeSign, FaGift, FaChartBar, FaHistory } from 'react-icons/fa';
import { AffiliateApiService } from '../../service/affiliateApiService';
import { useLoader } from '../../base/context/LoaderProvider';
import './affiliate.css';

export default function AffiliateEarnings() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const loader = useLoader();
  const navigate = useNavigate();

  useEffect(() => {
    loader.showLoading(true);
    AffiliateApiService.getEarnings()
      .then((res) => {
        loader.showLoading(false);
        if (res.status?.code === 200 && res.data) {
          setData(res.data);
        } else {
          setError(res.error?.message || 'Failed to load');
        }
      })
      .catch((err) => {
        loader.showLoading(false);
        if (err?.status === 403) {
          navigate('/', { replace: true });
          return;
        }
        setError(err?.message || 'Failed to load');
      });
  }, []);

  const formatDate = (d) => (d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '–');
  const formatMoney = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

  if (error && !data) {
    return (
      <div className="affiliate-page">
        <h1 className="affiliate-welcome">Earnings</h1>
        <p className="affiliate-error">{error}</p>
      </div>
    );
  }

  if (!data) return null;

  const monthlyTrend = data.monthlyTrend || [];
  const maxAmount = Math.max(1, ...monthlyTrend.map((m) => m.amount || 0));
  const recentCommissions = data.recentCommissions || [];

  return (
    <div className="affiliate-page">
      <h1 className="affiliate-welcome">Earnings</h1>
      <p className="affiliate-subtitle">Commission breakdown and monthly trend. Track every credit.</p>

      <div className="affiliate-cards" style={{ marginBottom: '1.75rem' }}>
        <div className="affiliate-card">
          <div className="affiliate-card-icon"><FaGift size={20} /></div>
          <div className="affiliate-card-body">
            <div className="affiliate-card-label">Registration bonus (₹{data.registrationBonusPerEligible || 500} per eligible seller)</div>
            <div className="affiliate-card-value">{formatMoney(data.registrationBonusTotal)}</div>
          </div>
        </div>
        <div className="affiliate-card">
          <div className="affiliate-card-icon"><FaChartBar size={20} /></div>
          <div className="affiliate-card-body">
            <div className="affiliate-card-label">Performance commission</div>
            <div className="affiliate-card-value">{formatMoney(data.performanceTotal)}</div>
          </div>
        </div>
        <div className="affiliate-card">
          <div className="affiliate-card-icon"><FaRupeeSign size={20} /></div>
          <div className="affiliate-card-body">
            <div className="affiliate-card-label">Total earnings</div>
            <div className="affiliate-card-value">{formatMoney(data.totalEarnings)}</div>
          </div>
        </div>
      </div>

      <div className="affiliate-referral-section">
        <h2 className="affiliate-section-title">Last 6 months trend</h2>
        <p className="affiliate-section-desc">Earnings added to your IM Cash by month.</p>
        {monthlyTrend.length > 0 ? (
          <div className="affiliate-chart-wrap">
            <div className="affiliate-chart-bar">
              {monthlyTrend.map((m) => (
                <div
                  key={m.month}
                  className="affiliate-chart-bar-item"
                  style={{ height: `${maxAmount ? Math.max(8, (100 * (m.amount || 0)) / maxAmount) : 0}%` }}
                  title={`${m.month}: ${formatMoney(m.amount)}`}
                />
              ))}
            </div>
            <div className="affiliate-chart-labels">
              {monthlyTrend.map((m) => (
                <span key={m.month} className="affiliate-chart-label-item">
                  {m.month}: <strong>{formatMoney(m.amount)}</strong>
                </span>
              ))}
            </div>
          </div>
        ) : (
          <p className="affiliate-empty">No commission data yet.</p>
        )}
      </div>

      <div className="affiliate-section">
        <h2 className="affiliate-section-title">
          <FaHistory size={16} style={{ marginRight: '0.5rem', verticalAlign: 'middle' }} />
          Recent commissions
        </h2>
        <p className="affiliate-section-desc">Earnings added to your IM Cash (in progress until you withdraw to bank).</p>
        {recentCommissions.length === 0 ? (
          <p className="affiliate-empty">No commission history yet.</p>
        ) : (
          <div className="affiliate-table-wrap">
            <table className="affiliate-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Type</th>
                  <th>Seller</th>
                  <th>Status</th>
                  <th>Amount</th>
                </tr>
              </thead>
              <tbody>
                {recentCommissions.map((c) => (
                  <tr key={c._id}>
                    <td>{formatDate(c.createdAt)}</td>
                    <td>{c.type === 'registration_bonus' ? 'Registration bonus' : 'Performance'}</td>
                    <td>{c.sellerName || '–'}</td>
                    <td>
                      <span className={`affiliate-badge ${c.status === 'credited' ? 'affiliate-badge-progress' : 'affiliate-badge-pending'}`}>
                        {c.status === 'credited' ? 'Progress' : c.status}
                      </span>
                    </td>
                    <td style={{ fontWeight: 600 }}>{formatMoney(c.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
